import React, { useState } from 'react';
import { Search, TrendingUp, TrendingDown, ShoppingCart, AlertCircle, RefreshCw, Plus } from 'lucide-react';
import Layout from '../components/Layout';
import Loading from '../components/Loading';
import { tradingService, getErrorMessage } from '../services/api';
import { formatCurrency } from '../utils/helpers';
import toast from 'react-hot-toast';

const TradingPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockPrice, setStockPrice] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [isAddingToAI, setIsAddingToAI] = useState(null); // AI 추천에 추가 중인 종목 코드
  
  const [orderData, setOrderData] = useState({
    orderType: 'BUY',
    priceType: 'MARKET',
    quantity: 1,
    price: 0,
  });

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('검색어를 입력해주세요.');
      return;
    }
    
    setIsSearching(true);
    setSearchResults([]);
    
    try {
      console.log('🔍 종목 검색 시작:', searchQuery);
      const response = await tradingService.searchStock(searchQuery);
      console.log('🔍 검색 응답 전체:', response);
      
      if (response && response.success) {
        const results = response.results || [];
        console.log('🔍 검색 결과:', results);
        
        setSearchResults(results);
        
        if (results.length > 0) {
          toast.success(`${results.length}개 종목을 찾았습니다.`);
        } else {
          toast('검색된 종목이 없습니다.');
        }
      } else {
        console.error('검색 응답 구조 이상:', response);
        toast.error(response?.error || '종목 검색에 실패했습니다.');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('검색 오류:', error);
      const errorMsg = getErrorMessage(error) || '종목 검색에 실패했습니다.';
      toast.error(errorMsg);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // 🤖 AI 추천에 종목 추가 함수
  const handleAddToAI = async (stock) => {
    if (!stock || !stock.stock_code) {
      toast.error('유효하지 않은 종목입니다.');
      return;
    }

    const confirmMessage = `${stock.stock_name} (${stock.stock_code})을 AI 추천 시스템에 추가하시겠습니까?\n\n이 종목은 "사용자분석" 섹터로 분류되어 AI가 분석한 후 추천 시스템에 포함됩니다.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsAddingToAI(stock.stock_code);
    
    try {
      console.log('🤖 AI 추천 추가 요청:', stock);
      
      // 10002 서비스로 요청 (임시 방안 - 추후 10009로 이관 예정)
      const response = await fetch('/analysis-api/add-user-stock', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          stock_code: stock.stock_code,
          stock_name: stock.stock_name,
          sector: '사용자분석'
        })
      });

      const result = await response.json();
      console.log('🤖 AI 추천 추가 응답:', result);

      if (result.success) {
        toast.success(`✅ ${stock.stock_name}이 AI 추천 시스템에 추가되었습니다!\n\nAI 분석이 완료되면 추천 목록에서 확인할 수 있습니다.`);
      } else {
        toast.error(result.message || 'AI 추천 추가에 실패했습니다.');
      }
    } catch (error) {
      console.error('AI 추천 추가 오류:', error);
      toast.error('AI 추천 시스템 연결에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsAddingToAI(null);
    }
  };

  const handleSelectStock = async (stock) => {
    console.log('📈 종목 선택:', stock);
    setSelectedStock(stock);
    setStockPrice(null);
    setIsLoadingPrice(true);
    
    try {
      const response = await tradingService.getStockPrice(stock.stock_code);
      console.log('💰 현재가 응답:', response);
      
      if (response && response.success) {
        setStockPrice(response);
        setOrderData(prev => ({
          ...prev,
          price: response.current_price || 0
        }));
        toast.success(`${stock.stock_name} 현재가 조회 완료`);
      } else {
        toast.error(response?.error || '현재가 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('현재가 조회 오류:', error);
      const errorMsg = getErrorMessage(error) || '현재가 조회에 실패했습니다.';
      toast.error(errorMsg);
    } finally {
      setIsLoadingPrice(false);
    }
  };

  const handleRefreshPrice = async () => {
    if (!selectedStock) return;
    
    setIsLoadingPrice(true);
    try {
      const response = await tradingService.getStockPrice(selectedStock.stock_code);
      if (response && response.success) {
        setStockPrice(response);
        setOrderData(prev => ({
          ...prev,
          price: response.current_price || prev.price
        }));
        toast.success('현재가가 업데이트되었습니다.');
      } else {
        toast.error(response?.error || '현재가 조회에 실패했습니다.');
      }
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      toast.error(errorMsg);
    } finally {
      setIsLoadingPrice(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedStock || !stockPrice) {
      toast.error('종목을 선택하고 현재가를 확인해주세요.');
      return;
    }

    if (!orderData.quantity || orderData.quantity < 1) {
      toast.error('주문 수량을 확인해주세요.');
      return;
    }

    if (orderData.priceType === 'LIMIT' && (!orderData.price || orderData.price <= 0)) {
      toast.error('주문 가격을 확인해주세요.');
      return;
    }

    // 주문 확인
    const orderTypeText = orderData.orderType === 'BUY' ? '매수' : '매도';
    const priceTypeText = orderData.priceType === 'MARKET' ? '시장가' : '지정가';
    const priceText = orderData.priceType === 'MARKET' 
      ? `현재가 ${stockPrice.current_price?.toLocaleString()}원`
      : `${orderData.price.toLocaleString()}원`;
    
    const confirmMessage = `${selectedStock.stock_name} ${orderData.quantity}주를 ${priceTypeText} ${orderTypeText} 하시겠습니까?\n\n가격: ${priceText}\n예상 거래금액: ${((orderData.priceType === 'MARKET' ? stockPrice.current_price : orderData.price) * orderData.quantity).toLocaleString()}원`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsPlacingOrder(true);
    try {
      const order = {
        stock_code: selectedStock.stock_code,
        order_type: orderData.orderType,
        price_type: orderData.priceType,
        quantity: parseInt(orderData.quantity),
        price: orderData.priceType === 'LIMIT' ? parseFloat(orderData.price) : undefined,
        initiator_type: 'MANUAL',
        order_reason: `수동 ${orderTypeText} 주문`
      };

      console.log('📋 주문 데이터:', order);

      const response = await tradingService.placeOrder(order);
      console.log('✅ 주문 응답:', response);
      
      if (response && response.success) {
        toast.success(`${orderTypeText} 주문이 성공적으로 접수되었습니다!\n주문번호: ${response.order_number || 'N/A'}`);
        
        // 주문 후 폼 초기화
        setOrderData(prev => ({
          ...prev,
          quantity: 1,
          price: stockPrice.current_price || 0,
        }));
      } else {
        toast.error(response?.error || '주문 실행에 실패했습니다.');
      }
    } catch (error) {
      console.error('주문 실행 오류:', error);
      const errorMsg = getErrorMessage(error) || '주문 실행에 실패했습니다.';
      toast.error(errorMsg);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const handleOrderDataChange = (field, value) => {
    setOrderData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getChangeColor = (value) => {
    const num = parseFloat(value);
    if (num > 0) return 'text-red-600';
    if (num < 0) return 'text-blue-600';
    return 'text-gray-600';
  };

  const formatChangeRate = (rate) => {
    const num = parseFloat(rate);
    return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* 헤더 */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">주식 거래</h1>
          <p className="text-gray-600 mt-1">종목을 검색하고 매수/매도 주문을 실행하세요</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 종목 검색 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">종목 검색</h2>
            
            <div className="flex space-x-2 mb-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="종목명 또는 종목코드 입력 (예: 삼성전자, 005930)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isSearching ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Search className="w-4 h-4" />
                )}
                <span className="font-medium">검색</span>
              </button>
            </div>

            {/* 검색 결과 */}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {isSearching && (
                <div className="flex justify-center py-8">
                  <Loading message="종목을 검색하는 중..." />
                </div>
              )}
              
              {!isSearching && Array.isArray(searchResults) && searchResults.length > 0 && (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    검색 결과 {searchResults.length}개
                  </p>
                  {searchResults.map((stock, index) => (
                    <div
                      key={`${stock.stock_code}-${index}`}
                      className={`p-4 border rounded-lg transition-all hover:shadow-md ${
                        selectedStock?.stock_code === stock.stock_code 
                          ? 'border-blue-500 bg-blue-50 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        {/* 종목 정보 (clickable) */}
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => handleSelectStock(stock)}
                        >
                          <p className="font-semibold text-gray-900">{stock.stock_name}</p>
                          <p className="text-sm text-gray-500 font-mono">{stock.stock_code}</p>
                        </div>
                        
                        {/* 버튼 그룹 */}
                        <div className="flex items-center space-x-2">
                          {/* AI 추천 추가 버튼 */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // 종목 선택 이벤트 방지
                              handleAddToAI(stock);
                            }}
                            disabled={isAddingToAI === stock.stock_code}
                            className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            title="AI 추천 시스템에 추가"
                          >
                            {isAddingToAI === stock.stock_code ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Plus className="w-4 h-4" />
                            )}
                            <span className="font-medium">AI 추가</span>
                          </button>
                          
                          {/* 종목 선택 아이콘 */}
                          <div 
                            className="cursor-pointer p-2 rounded-lg hover:bg-gray-100"
                            onClick={() => handleSelectStock(stock)}
                            title="종목 선택"
                          >
                            <TrendingUp className="w-5 h-5 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {!isSearching && searchQuery && (!Array.isArray(searchResults) || searchResults.length === 0) && (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">검색 결과가 없습니다</p>
                  <p className="text-gray-500 text-sm mt-1">다른 검색어로 시도해보세요</p>
                </div>
              )}
              
              {!searchQuery && !isSearching && (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">종목명 또는 코드를 입력하세요</p>
                  <div className="text-sm text-gray-500 mt-2 space-y-1">
                    <p>예시: 삼성전자, 005930, SK하이닉스, 000660</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 주문 입력 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">주문 입력</h2>
            
            {selectedStock && stockPrice ? (
              <div className="space-y-4">
                {/* 선택된 종목 정보 */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {selectedStock.stock_name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {selectedStock.stock_code}
                      </p>
                    </div>
                    <button
                      onClick={handleRefreshPrice}
                      disabled={isLoadingPrice}
                      className="flex items-center space-x-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isLoadingPrice ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      <span>새로고침</span>
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">현재가</span>
                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(stockPrice.current_price)}
                      </p>
                    </div>
                    
                    {stockPrice.change_amount && (
                      <div>
                        <span className="text-gray-600">등락</span>
                        <div className={`text-sm font-medium ${getChangeColor(stockPrice.change_amount)}`}>
                          <div className="flex items-center space-x-1">
                            {parseFloat(stockPrice.change_amount) > 0 ? (
                              <TrendingUp className="w-4 h-4" />
                            ) : parseFloat(stockPrice.change_amount) < 0 ? (
                              <TrendingDown className="w-4 h-4" />
                            ) : null}
                            <span>
                              {stockPrice.change_amount > 0 ? '+' : ''}
                              {formatCurrency(stockPrice.change_amount)}
                            </span>
                          </div>
                          <p className={getChangeColor(stockPrice.change_rate)}>
                            {formatChangeRate(stockPrice.change_rate)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 주문 타입 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">주문 구분</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleOrderDataChange('orderType', 'BUY')}
                      className={`py-3 px-4 rounded-lg font-semibold transition-all ${
                        orderData.orderType === 'BUY'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      매수
                    </button>
                    <button
                      onClick={() => handleOrderDataChange('orderType', 'SELL')}
                      className={`py-3 px-4 rounded-lg font-semibold transition-all ${
                        orderData.orderType === 'SELL'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      매도
                    </button>
                  </div>
                </div>

                {/* 주문 방식 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">주문 방식</label>
                  <select
                    value={orderData.priceType}
                    onChange={(e) => handleOrderDataChange('priceType', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="MARKET">시장가 (즉시 체결)</option>
                    <option value="LIMIT">지정가 (가격 설정)</option>
                  </select>
                </div>

                {/* 주문 수량 */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">주문 수량</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={orderData.quantity}
                    onChange={(e) => handleOrderDataChange('quantity', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="주문 수량을 입력하세요"
                  />
                </div>

                {/* 주문 가격 (지정가일 때만) */}
                {orderData.priceType === 'LIMIT' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">주문 가격</label>
                    <input
                      type="number"
                      min="0"
                      step="100"
                      value={orderData.price}
                      onChange={(e) => handleOrderDataChange('price', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="주문 가격을 입력하세요"
                    />
                  </div>
                )}

                {/* 예상 금액 */}
                {stockPrice && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">예상 거래금액</span>
                        <span className="text-lg font-bold text-gray-900">
                          {formatCurrency(
                            (orderData.priceType === 'MARKET' ? stockPrice.current_price : (orderData.price || 0)) * (orderData.quantity || 0)
                          )}
                        </span>
                      </div>
                      {orderData.priceType === 'MARKET' && (
                        <p className="text-xs text-blue-700">
                          * 시장가는 현재가 기준 예상금액이며, 실제 체결가와 다를 수 있습니다.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* 주문 버튼 */}
                <button
                  onClick={handlePlaceOrder}
                  disabled={isPlacingOrder || !stockPrice || !orderData.quantity}
                  className={`w-full flex items-center justify-center space-x-2 py-4 px-4 rounded-lg font-semibold text-lg transition-all ${
                    orderData.orderType === 'BUY'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isPlacingOrder ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>주문 처리 중...</span>
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      <span>
                        {orderData.orderType === 'BUY' ? '매수' : '매도'} 주문 실행
                      </span>
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="text-center py-16">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg font-medium">거래할 종목을 선택해주세요</p>
                <p className="text-gray-500 text-sm mt-1">
                  왼쪽에서 종목을 검색하고 선택하면<br />
                  주문을 입력할 수 있습니다.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 주의사항 */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">거래 주의사항</h3>
              <div className="text-sm text-yellow-700 space-y-1">
                <p>• <strong>시장가 주문:</strong> 현재 시장가격으로 즉시 체결됩니다.</p>
                <p>• <strong>지정가 주문:</strong> 설정한 가격에 도달했을 때 체결됩니다.</p>
                <p>• <strong>주문 시간:</strong> 장중(09:00-15:30)에만 주문이 처리됩니다.</p>
                <p>• <strong>AI 추천 추가:</strong> 선택한 종목을 AI가 분석하여 추천 시스템에 포함시킵니다.</p>
                <p>• <strong>투자 책임:</strong> 모든 투자 결정과 그에 따른 손익은 투자자 본인의 책임입니다.</p>
                <p>• <strong>정확한 정보:</strong> 최종 확인은 증권사 HTS/MTS에서 진행하시기 바랍니다.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TradingPage;